'use client';

import { useState, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  buildRegionLocaleSlug,
  isValidRegionSlug,
  serializeRegionPreference,
} from 'lib/region/regionLocale';
import { COUNTRY_NAME_FALLBACK } from 'lib/region/countryUtils';
import { REGION_GROUPS, getLanguageOptions } from 'lib/region/regionGroups';
import { analytics } from 'lib/analytics/analytics';
import styles from './choose-country-region-client.module.css';

function getCountryName(code: string): string {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return (
      displayNames.of(code.toUpperCase()) ?? COUNTRY_NAME_FALLBACK[code] ?? code
    );
  } catch {
    return COUNTRY_NAME_FALLBACK[code] ?? code;
  }
}

function buildRedirectUrl(ref: string | null, newSlug: string): string {
  if (!ref) return `/${newSlug}/`;

  const segments = ref.replace(/^\//, '').split('/');
  const firstSegment = segments[0];

  if (firstSegment && isValidRegionSlug(firstSegment)) {
    const rest = segments.slice(1).join('/');
    return `/${newSlug}/${rest}`;
  }

  return `/${newSlug}${ref}`;
}

const ChooseCountryRegion = () => {
  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') || null;
  const [search, setSearch] = useState('');
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);

  const handleSelect = useCallback(
    (countryCode: string, language?: string) => {
      analytics.track('Region Selector - CHANGE', {
        country: countryCode,
        language: language || 'default',
      });
      const slug = buildRegionLocaleSlug(countryCode, language);
      // Persist the choice so the proxy stops geo-bouncing this visitor off it.
      const preferenceCookie = serializeRegionPreference(
        slug,
        window.location.protocol === 'https:',
      );
      if (preferenceCookie) document.cookie = preferenceCookie;
      const destination = buildRedirectUrl(ref, slug);
      // Region switches need a full document navigation (shared internal route).
      window.location.assign(destination);
    },
    [ref],
  );

  const handleCountryClick = useCallback(
    (countryCode: string) => {
      const langOptions = getLanguageOptions(countryCode);

      if (langOptions.length === 0) {
        // Single language — navigate directly
        handleSelect(countryCode);
      } else {
        // Multi-language — toggle inline expand
        setExpandedCountry((prev) =>
          prev === countryCode ? null : countryCode,
        );
      }
    },
    [handleSelect],
  );

  const searchLower = search.toLowerCase().trim();

  const filteredGroups = REGION_GROUPS.map((group) => ({
    ...group,
    countries: group.countries.filter((code) => {
      if (!searchLower) return true;
      const name = getCountryName(code);
      return (
        name.toLowerCase().includes(searchLower) ||
        code.toLowerCase().includes(searchLower)
      );
    }),
  })).filter((group) => group.countries.length > 0);

  const sortedGroups = filteredGroups.map((group) => ({
    ...group,
    countries: [...group.countries].sort((a, b) =>
      getCountryName(a).localeCompare(getCountryName(b)),
    ),
  }));

  return (
    <main className={styles.pageWrapper}>
      <h1 className={styles.pageTitle}>Choose Your Country or Region</h1>

      <div className={styles.searchWrapper}>
        <input
          type="text"
          placeholder="Search for a country or region"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      <div className={styles.contentWrapper}>
        {sortedGroups.length === 0 && (
          <p className={styles.noResults}>
            No countries found for &ldquo;{search}&rdquo;
          </p>
        )}

        {sortedGroups.map((group) => (
          <section className={styles.regionGroupSection} key={group.label}>
            <h2 className={styles.groupTitle}>{group.label}</h2>
            <div className={styles.countryGrid}>
              {group.countries.map((code) => {
                const langOptions = getLanguageOptions(code);
                const isExpanded = expandedCountry === code;

                return (
                  <div className={styles.countryItem} key={code}>
                    <button
                      className={styles.countryButton}
                      onClick={() => handleCountryClick(code)}
                      suppressHydrationWarning
                    >
                      {getCountryName(code)}
                    </button>

                    {isExpanded && langOptions.length > 0 && (
                      <div className={styles.languageOptions}>
                        {langOptions.map((lang) => (
                          <button
                            className={styles.languageLink}
                            key={lang.code}
                            onClick={() => handleSelect(code, lang.code)}
                          >
                            {lang.nativeName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <section className={styles.globalSection}>
          <p className={styles.globalLabel}>Can&apos;t find your country?</p>
          <button
            className={styles.globalButton}
            onClick={() => handleSelect('GLOBAL')}
          >
            Global Store
          </button>
        </section>
      </div>
    </main>
  );
};

export default ChooseCountryRegion;
